import React, { useState, useEffect } from 'react';

const DEFAULTS = {
  // Service Killer
  disable_services: {
    api: true,
    ftp: true,
    telnet: true,
    www: true,
    winbox: false,
    ssh: false,
  },
  // Port Changes
  custom_ports: {
    winbox: 8291,
    ssh: 22,
    api: 8728,
    www: 80,
  },
  // Brute Force Protection
  brute_force: {
    enabled: true,
    max_attempts: 3,
    ban_time: '10m',
    detect_time: '5m',
    whitelist: '192.168.88.0/24',
  },
  // Additional Hardening
  hardening: {
    disable_ipv6: false,
    disable_ipv6_firewall: true,
    disable_neighbor_discovery: false,
    disable_macs_server: true,
    disable_upnp: true,
    disable_cloud: true,
    secure_mode: false,
  },
};

export default function StepSecurity({ data, onChange, onNext, onBack }) {
  const [form, setForm] = useState(data || DEFAULTS);
  const [activeTab, setActiveTab] = useState('services');

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const update = (key, value) => {
    const updated = { ...form, [key]: value };
    setForm(updated);
    onChange(updated);
  };

  const toggleService = (service) => {
    const updated = { ...form.disable_services, [service]: !form.disable_services[service] };
    update('disable_services', updated);
  };

  const updatePort = (service, value) => {
    const updated = { ...form.custom_ports, [service]: parseInt(value) || 0 };
    update('custom_ports', updated);
  };

  const updateBruteForce = (key, value) => {
    const updated = { ...form.brute_force, [key]: value };
    update('brute_force', updated);
  };

  const toggleHardening = (key) => {
    const updated = { ...form.hardening, [key]: !form.hardening[key] };
    update('hardening', updated);
  };

  const services = [
    { key: 'api', name: 'API', port: 8728, icon: '🔌', risk: 'สูง', desc: 'REST API สำหรับจัดการ Router' },
    { key: 'ftp', name: 'FTP', port: 21, icon: '📁', risk: 'สูง', desc: 'File Transfer Protocol' },
    { key: 'telnet', name: 'Telnet', port: 23, icon: '💻', risk: 'สูงมาก', desc: 'Remote Access แบบไม่เข้ารหัส' },
    { key: 'www', name: 'WWW/HTTP', port: 80, icon: '🌐', risk: 'สูง', desc: 'Web Interface' },
    { key: 'winbox', name: 'Winbox', port: 8291, icon: '🖥️', risk: 'ปานกลาง', desc: 'WinBox GUI Management' },
    { key: 'ssh', name: 'SSH', port: 22, icon: '🔑', risk: 'ปานกลาง', desc: 'Secure Shell Remote Access' },
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">🔒 ความปลอดภัย (Security Hardening)</h2>
        <p className="text-gray-500 dark:text-gray-400">One-Click Security: ปิด Service ที่ไม่ใช้, เปลี่ยนพอร์ต, ป้องกัน Brute Force</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'services', label: '🔌 ปิด Service', desc: 'Service Killer' },
          { key: 'ports', label: '🔢 เปลี่ยนพอร์ต', desc: 'Custom Ports' },
          { key: 'brute', label: '🛡️ Brute Force', desc: 'ป้องกันเดารหัส' },
          { key: 'extra', label: '⚙️ เพิ่มเติม', desc: 'Hardening' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 p-3 rounded-xl border-2 text-left transition-all ${
              activeTab === tab.key
                ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
            }`}
          >
            <div className="font-bold text-sm">{tab.label}</div>
            <div className="text-xs opacity-70">{tab.desc}</div>
          </button>
        ))}
      </div>

      {/* Service Killer Tab */}
      {activeTab === 'services' && (
        <div>
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 rounded-xl">
            <h3 className="font-bold text-red-800 dark:text-red-300 mb-1">⚠️ Service Killer</h3>
            <p className="text-xs text-red-600 dark:text-red-400">ปิด Service ที่ไม่ได้ใช้ ลดช่องโหว่ที่ถูกโจมตี</p>
          </div>

          <div className="space-y-3">
            {services.map(svc => {
              const isDisabled = form.disable_services[svc.key];
              const riskColors = { 'สูงมาก': 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300', 'สูง': 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300', 'ปานกลาง': 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' };
              return (
                <label key={svc.key} className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  isDisabled
                    ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800/50'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{svc.icon}</span>
                    <div>
                      <span className="font-bold text-gray-900 dark:text-white">{svc.name}</span>
                      <span className="ml-2 text-xs text-gray-400">พอร์ต {svc.port}</span>
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${riskColors[svc.risk]}`}>ความเสี่ยง: {svc.risk}</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{svc.desc}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${isDisabled ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {isDisabled ? '🔴 ปิด' : '🟢 เปิด'}
                    </span>
                    <input type="checkbox" checked={isDisabled} onChange={() => toggleService(svc.key)} className="w-5 h-5 text-rose-600 rounded" />
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Custom Ports Tab */}
      {activeTab === 'ports' && (
        <div>
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 rounded-xl">
            <h3 className="font-bold text-amber-800 dark:text-amber-300 mb-1">🔢 เปลี่ยนพอร์ตเริ่มต้น</h3>
            <p className="text-xs text-amber-600 dark:text-amber-400">เปลี่ยนพอร์ตจากค่าเริ่มต้น ป้องกันการสแกนพอร์ตอัตโนมัติ</p>
          </div>

          <div className="space-y-3">
            {[
              { key: 'winbox', name: 'Winbox', default: 8291, icon: '🖥️' },
              { key: 'ssh', name: 'SSH', default: 22, icon: '🔑' },
              { key: 'api', name: 'API', default: 8728, icon: '🔌' },
              { key: 'www', name: 'WWW/HTTP', default: 80, icon: '🌐' },
            ].map(svc => (
              <div key={svc.key} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{svc.icon}</span>
                  <div>
                    <span className="font-bold text-gray-900 dark:text-white">{svc.name}</span>
                    <p className="text-xs text-gray-400">ค่าเริ่มต้น: {svc.default}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="65535"
                    value={form.custom_ports[svc.key]}
                    onChange={e => updatePort(svc.key, e.target.value)}
                    className="input-field w-24 text-center"
                  />
                  {form.custom_ports[svc.key] !== svc.default && (
                    <span className="text-xs text-green-600 dark:text-green-400">✓ เปลี่ยนแล้ว</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Brute Force Protection Tab */}
      {activeTab === 'brute' && (
        <div>
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/50 rounded-xl">
            <h3 className="font-bold text-green-800 dark:text-green-300 mb-1">🛡️ ป้องกัน Brute Force</h3>
            <p className="text-xs text-green-600 dark:text-green-400">บล็อก IP ที่พยายามเดารหัสผ่านเกินจำนวนที่กำหนด</p>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 cursor-pointer">
              <div>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">เปิดใช้งาน Brute Force Protection</span>
                <p className="text-xs text-gray-400 mt-0.5">สร้าง Firewall Rules อัตโนมัติ</p>
              </div>
              <input type="checkbox" checked={form.brute_force.enabled} onChange={e => updateBruteForce('enabled', e.target.checked)} className="w-5 h-5 text-green-600 rounded" />
            </label>

            {form.brute_force.enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">จำนวนครั้งก่อนบล็อก</label>
                  <select value={form.brute_force.max_attempts} onChange={e => updateBruteForce('max_attempts', parseInt(e.target.value))} className="input-field">
                    <option value={2}>2 ครั้ง</option>
                    <option value={3}>3 ครั้ง</option>
                    <option value={5}>5 ครั้ง</option>
                    <option value={10}>10 ครั้ง</option>
                  </select>
                </div>
                <div>
                  <label className="label">ระยะเวลาแบน (Ban Time)</label>
                  <select value={form.brute_force.ban_time} onChange={e => updateBruteForce('ban_time', e.target.value)} className="input-field">
                    <option value="5m">5 นาที</option>
                    <option value="10m">10 นาที</option>
                    <option value="30m">30 นาที</option>
                    <option value="1h">1 ชั่วโมง</option>
                    <option value="1d">1 วัน</option>
                    <option value="forever">ถาวร</option>
                  </select>
                </div>
                <div>
                  <label className="label">ระยะเวลาตรวจจับ (Detect Time)</label>
                  <select value={form.brute_force.detect_time} onChange={e => updateBruteForce('detect_time', e.target.value)} className="input-field">
                    <option value="1m">1 นาที</option>
                    <option value="5m">5 นาที</option>
                    <option value="10m">10 นาที</option>
                    <option value="15m">15 นาที</option>
                  </select>
                </div>
                <div>
                  <label className="label">IP ที่ยกเว้น (Whitelist)</label>
                  <input type="text" value={form.brute_force.whitelist} onChange={e => updateBruteForce('whitelist', e.target.value)} className="input-field" placeholder="192.168.88.0/24" />
                </div>
              </div>
            )}

            <div className="p-3 bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-800/50 rounded-lg">
              <p className="text-xs text-green-700 dark:text-green-300">
                <strong>💡 วิธีทำงาน:</strong> สร้าง Address List "blacklist" และ Firewall Rules 3 กฎ:<br/>
                1. ยกเว้น IP Whitelist<br/>
                2. เพิ่ม IP เข้า blacklist เมื่อ login ผิด {form.brute_force.max_attempts} ครั้งใน {form.brute_force.detect_time}<br/>
                3. บล็อก IP ใน blacklist เป็นเวลา {form.brute_force.ban_time}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Extra Hardening Tab */}
      {activeTab === 'extra' && (
        <div>
          <div className="mb-4 p-4 bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800/50 rounded-xl">
            <h3 className="font-bold text-purple-800 dark:text-purple-300 mb-1">⚙️ Hardening เพิ่มเติม</h3>
            <p className="text-xs text-purple-600 dark:text-purple-400">ตั้งค่าความปลอดภัยเพิ่มเติม</p>
          </div>

          <div className="space-y-3">
            {[
              { key: 'disable_ipv6', name: 'ปิด IPv6', desc: 'ถ้าไม่ได้ใช้ IPv6', icon: '🌐' },
              { key: 'disable_ipv6_firewall', name: 'เพิ่มกฎ IPv6 Firewall', desc: 'บล็อก IPv6 ทั้งหมด', icon: '🛡️' },
              { key: 'disable_neighbor_discovery', name: 'ปิด Neighbor Discovery', desc: 'ป้องกันการค้นหา Router', icon: '🔍' },
              { key: 'disable_macs_server', name: 'ปิด MAC Server', desc: 'ป้องกัน Winbox ผ่าน MAC', icon: '🔌' },
              { key: 'disable_upnp', name: 'ปิด UPnP', desc: 'ป้องกัน Port Forward อัตโนมัติ', icon: '📡' },
              { key: 'disable_cloud', name: 'ปิด MikroTik Cloud', desc: 'ปิดการเชื่อมต่อ Cloud', icon: '☁️' },
              { key: 'secure_mode', name: 'Secure Mode', desc: 'ปิด HTTP API, จำกัด Winbox/SSH เฉพาะ LAN', icon: '🔒' },
            ].map(item => (
              <label key={item.key} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 cursor-pointer hover:border-purple-200 dark:hover:border-purple-700 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{item.icon}</span>
                  <div>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{item.name}</span>
                    <p className="text-xs text-gray-400">{item.desc}</p>
                  </div>
                </div>
                <input type="checkbox" checked={form.hardening[item.key]} onChange={() => toggleHardening(item.key)} className="w-5 h-5 text-purple-600 rounded" />
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-secondary">← ย้อนกลับ</button>
        <button onClick={onNext} className="btn-primary">ถัดไป</button>
      </div>
    </div>
  );
}
